/**
 * useExamAuth — the examination-system's app-local auth hook.
 *
 * This is a thin compatibility layer on top of the nexus-auth v4 SDK's
 * `useAuth()`. It re-exposes the fields and helpers the exam app has been
 * using since the v3 era (roles array, hasAnyRole, user) so that the
 * migration to v4 doesn't require rewriting every call site.
 *
 * Under the hood, everything comes from the v4 SDK: auth state lives in
 * memory, tokens live in HttpOnly cookies, and we decode the current
 * `accessToken` JWT just to surface role/permission arrays that v4's
 * public `useAuth` doesn't return directly.
 */
import { useMemo } from 'react';
import { useAuth } from '@azyntor-labs/nexus-auth';

const SERVICE_NAME = import.meta.env.VITE_SERVICE_NAME || 'exam';

/**
 * Best-effort JWT payload decode. Never throws. Used only to surface
 * `roles`/`permissions` arrays derived from the access token claims. We
 * never verify — verification is the server's job.
 */
function decodeJwtClaims(token: string | null): Record<string, unknown> | null {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = normalized.length % 4;
    const padded = pad ? normalized + '='.repeat(4 - pad) : normalized;
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

function extractRolesFromClaims(claims: Record<string, unknown> | null, serviceName: string): string[] {
  if (!claims) return [];
  const rbs = claims.roles_by_service as Record<string, string[]> | undefined;
  if (!rbs) return [];
  const out: string[] = [];
  if (Array.isArray(rbs[serviceName])) out.push(...rbs[serviceName]);
  if (Array.isArray(rbs.global)) out.push(...rbs.global);
  return Array.from(new Set(out));
}

function extractPermissionsFromClaims(claims: Record<string, unknown> | null, serviceName: string): string[] {
  if (!claims) return [];
  const pbs = claims.permissions_by_service as Record<string, string[]> | undefined;
  if (!pbs) return [];
  const out: string[] = [];
  if (Array.isArray(pbs[serviceName])) out.push(...pbs[serviceName]);
  if (Array.isArray(pbs.global)) out.push(...pbs.global);
  return Array.from(new Set(out));
}

export interface ExamAuthHookReturn {
  user: ReturnType<typeof useAuth>['user'];
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  roles: string[];
  permissions: string[];
  login: ReturnType<typeof useAuth>['login'];
  logout: ReturnType<typeof useAuth>['logout'];
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasPermission: (permission: string) => boolean;
}

/**
 * App-local auth hook. Prefer this over the SDK's `useAuth()` when you
 * need `roles`/`hasAnyRole` — otherwise the two are equivalent.
 */
export function useExamAuth(): ExamAuthHookReturn {
  const auth = useAuth();
  const { user, accessToken, isAuthenticated, isLoading, login, logout } = auth;

  const { roles, permissions } = useMemo(() => {
    const claims = decodeJwtClaims(accessToken);
    return {
      roles: extractRolesFromClaims(claims, SERVICE_NAME),
      permissions: extractPermissionsFromClaims(claims, SERVICE_NAME),
    };
  }, [accessToken]);

  const hasRole = (role: string) => roles.includes(role);
  const hasAnyRole = (list: string[]) => list.some((r) => roles.includes(r));
  const hasPermission = (permission: string) => {
    if (roles.includes('system_admin')) return true;
    if (permissions.includes('*:*:*')) return true;
    return permissions.includes(permission);
  };

  return {
    user,
    accessToken,
    isAuthenticated,
    isLoading,
    roles,
    permissions,
    login,
    logout,
    hasRole,
    hasAnyRole,
    hasPermission,
  };
}
